/**
 * The view page's state as pure rules (2.K deliverable 9): what the URL carries, which view is
 * on screen, whether it differs from the saved one, and what a save sends. Nothing here throws.
 */
import type { SavedViewV1 } from '@codex/contracts';
import { type ColumnDef, defaultColumnKeys } from '../../catalogue/model/columns.js';
import {
  type CatalogueSearch,
  type CatalogueView,
  parseCatalogueSearch,
  queryKey,
  queryOf,
  type ReadResult,
  readView,
} from '../../catalogue/model/view.js';

/** The catalogue's search plus `edited`: the URL carries an unsaved edit of the saved view. */
export interface ViewPageSearch extends CatalogueSearch {
  edited?: true | undefined;
}

/** 1.N-2's parser plus `edited: true` when present. `edited` is always named (router merge). */
export function parseViewPageSearch(raw: unknown): ViewPageSearch {
  const base = parseCatalogueSearch(raw);
  const edited =
    typeof raw === 'object' && raw !== null && (raw as { edited?: unknown }).edited === true;
  return { ...base, edited: edited ? true : undefined };
}

/** The saved view as a catalogue search (an empty column list is the default columns). */
export function savedSearch(saved: Pick<SavedViewV1, 'query' | 'columns'>): CatalogueSearch {
  const out: CatalogueSearch = {};
  if (saved.query.q !== undefined && saved.query.q !== '') out.q = saved.query.q;
  if (saved.query.filters.length > 0) out.filters = [...saved.query.filters];
  if (saved.query.sort.length > 0) out.sort = [...saved.query.sort];
  if (saved.columns.length > 0) out.columns = [...saved.columns];
  return out;
}

/** The view a stored view stands for, for a table with these columns. */
export function savedAsView(
  saved: Pick<SavedViewV1, 'query' | 'columns'>,
  columns: readonly ColumnDef[],
): CatalogueView {
  return readView(savedSearch(saved), columns).view;
}

/** The view on screen with how many settings did not fit this table (see `workingView`). */
export function readWorking(
  saved: Pick<SavedViewV1, 'query' | 'columns'>,
  search: ViewPageSearch,
  columns: readonly ColumnDef[],
): ReadResult {
  return readView(search.edited === true ? search : savedSearch(saved), columns);
}

/**
 * The view on screen: the saved view on the bare URL, the search's view once `edited` is set. So
 * reload, Back and a pasted link keep an unsaved edit.
 */
export function workingView(
  saved: Pick<SavedViewV1, 'query' | 'columns'>,
  search: ViewPageSearch,
  columns: readonly ColumnDef[],
): CatalogueView {
  return readWorking(saved, search, columns).view;
}

const sameKeys = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((key, index) => key === b[index]);

/** True when the working view's query or resolved columns differ from the saved view's. */
export function isDirty(
  saved: Pick<SavedViewV1, 'query' | 'columns' | 'typeKey'>,
  working: CatalogueView,
  columns: readonly ColumnDef[],
): boolean {
  const stored = savedAsView(saved, columns);
  return (
    queryKey(stored, saved.typeKey) !== queryKey(working, saved.typeKey) ||
    !sameKeys(stored.columns, working.columns)
  );
}

/** What a save sends: the query without paging, and `[]` when the columns are the default. */
export function toSavedPayload(
  view: CatalogueView,
  columns: readonly ColumnDef[],
): { query: ReturnType<typeof queryOf>; columns: string[] } {
  return {
    query: queryOf(view),
    columns: sameKeys(view.columns, defaultColumnKeys(columns)) ? [] : [...view.columns],
  };
}

export type ToolbarContext =
  | { readonly mode: 'catalogue'; readonly typeKey: string; readonly view: CatalogueSearch }
  | { readonly mode: 'view'; readonly viewId: string; readonly view: ViewPageSearch };

export interface RouteMatchLike {
  readonly routeId: string;
  readonly params: Readonly<Record<string, string | undefined>>;
  readonly search: unknown;
}

/**
 * Where the catalogue toolbar is drawn: on a type's table, or on a view's page; elsewhere null
 * (the toolbar renders nothing). `view` is the page's URL search; the toolbar reads its columns.
 */
export function toolbarContext(match: RouteMatchLike | null | undefined): ToolbarContext | null {
  if (!match) return null;
  if (match.routeId === '/catalogue/$typeKey') {
    const typeKey = match.params.typeKey;
    if (typeKey === undefined) return null;
    return { mode: 'catalogue', typeKey, view: parseCatalogueSearch(match.search) };
  }
  if (match.routeId === '/views/$viewId') {
    const viewId = match.params.viewId;
    if (viewId === undefined) return null;
    return { mode: 'view', viewId, view: parseViewPageSearch(match.search) };
  }
  return null;
}
